import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Droplet, Loader2, Lock } from 'lucide-react';
import type { VerifyPinResponse } from '../../../shared/contracts';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { supabase } from '../lib/supabaseClient';
import { DEMO_ROLES } from '../constants/demoRoles';
import type { GetDemoRolesResponse } from '../../../shared/contracts';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, login, loginWithSupabase } = useAuth();
  const [roles, setRoles] = useState(DEMO_ROLES);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [step, setStep] = useState<'method' | 'role' | 'demo-pin'>('method');
  const [isLoggedOut, setIsLoggedOut] = useState((location.state as any)?.justLoggedOut ?? false);
  const [pinCode, setPinCode] = useState('');

  // Check if we're in Vibecode preview, staging, or local development environment
  const isVibecodePreview = React.useMemo(() => {
    const origin = window.location.origin;
    // Show demo in:
    // 1. Vibecode preview (preview-*.vibecode.run)
    // 2. Staging (test.aquaflow.app)
    // 3. Local development (localhost:*, 127.0.0.1:*, or local IP 192.168.*, 10.*, 172.16-31.*)
    const isVibecodeEnv = (origin.includes('preview-') && origin.includes('vibecode.run')) ||
                          origin.includes('test.aquaflow.app');
    const isLocalhost = origin.includes('localhost') || origin.includes('127.0.0.1');
    const isLocalIP = /192\.168\.\d+\.\d+/.test(origin) ||
                      /10\.\d+\.\d+\.\d+/.test(origin) ||
                      /172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+/.test(origin);

    return isVibecodeEnv || isLocalhost || isLocalIP;
  }, []);

  // Check for maintenance mode on page load
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        console.log('[Login] Checking maintenance mode status...');
        const maintenancePinEntered = sessionStorage.getItem('maintenancePinEntered') === 'true';

        const settingsResponse = await api.get<any>('/app-settings');
        if (settingsResponse?.success && settingsResponse?.settings?.maintenanceMode && !maintenancePinEntered) {
          console.log('[Login] App is in maintenance mode, redirecting to maintenance screen');
          navigate('/maintenance-mode');
          return;
        }
      } catch (error) {
        console.warn('[Login] Failed to check maintenance mode:', error);
        // Continue with normal login flow if we can't check
      }
    };

    checkMaintenanceMode();
  }, [navigate]);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      console.log('[Login] User already logged in, redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Reset logged out flag after 2 seconds so new OAuth sessions are allowed
  useEffect(() => {
    console.log('[Login] isLoggedOut state:', isLoggedOut);
    if (isLoggedOut) {
      const timer = setTimeout(() => {
        console.log('[Login] Resetting logout flag after timeout');
        setIsLoggedOut(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isLoggedOut]);

  // Check for OAuth callback on page load
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if we have a session from OAuth redirect
      const { data: { session }, error } = await supabase.auth.getSession();

      if (session && !error) {
        console.log('[Login] Found OAuth session:', session.user.email);
        try {
          console.log('[Login] Auto-logging in user from OAuth callback:', session.user.email);
          // Just navigate to dashboard - if locked, overlay will show
          await loginWithSupabase(session);
          navigate('/dashboard');
        } catch (err: any) {
          // Check if error is due to account being locked
          if (err.message === 'Account locked') {
            console.log('[Login] User account is locked, already redirected');
            return;
          }
          console.error('[Login] OAuth callback error:', err);
          const errorMsg = err.message?.includes('locked') ?
            'Your account is locked. Please contact your company administrator.' :
            'Failed to complete sign in. Please try again.';
          setError(errorMsg);
        }
      } else if (error) {
        console.log('[Login] No valid session or error:', error);
      }
    };

    // Listen for auth state changes (for OAuth redirects)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[Login] Auth state changed:', event, 'Has session:', !!session, 'Email:', session?.user?.email);

      // Don't auto-login if user just logged out
      if (isLoggedOut) {
        console.log('[Login] Ignoring auth state change because user just logged out');
        return;
      }

      // Only auto-login if we're signing in via OAuth
      // Don't auto-login on other state changes (like sign out)
      if (event === 'SIGNED_IN' && session && session.user?.email) {
        console.log('[Login] SIGNED_IN event detected, attempting auto-login for:', session.user.email);
        try {
          console.log('[Login] Auto-logging in user from state change:', session.user.email);
          // Just navigate to dashboard - if locked, overlay will show
          await loginWithSupabase(session);
          navigate('/dashboard');
        } catch (err: any) {
          // Check if error is due to account being locked
          if (err.message === 'Account locked') {
            console.log('[Login] User account is locked, already redirected');
            return;
          }
          console.error('[Login] Auth state change error:', err);
          const errorMsg = err.message?.includes('locked') ?
            'Your account is locked. Please contact your company administrator.' :
            'Failed to complete sign in. Please try again.';
          setError(errorMsg);
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('[Login] SIGNED_OUT event detected, user should stay on login page');
      } else if (event === 'INITIAL_SESSION') {
        console.log('[Login] INITIAL_SESSION event, has session:', !!session);
      } else {
        console.log('[Login] Ignoring auth event:', event);
      }
    });

    handleOAuthCallback();

    return () => {
      subscription.unsubscribe();
    };
  }, [loginWithSupabase, navigate, isLoggedOut]);

  // Fetch demo roles when user selects demo mode
  // Uses static fallback if API call fails (ensures roles always available)
  useEffect(() => {
    if (step === 'role') {
      fetchRoles();
    }
  }, [step]);

  const fetchRoles = async () => {
    try {
      const response = await api.get<GetDemoRolesResponse>('/demo-auth/roles');
      setRoles(response.roles);
    } catch (err) {
      console.error('Failed to fetch roles from API, using static fallback:', err);
      // Use static DEMO_ROLES as fallback - ensures app works even if backend is down
      setRoles(DEMO_ROLES);
    }
  };

  const handleDemoModeClick = async () => {
    try {
      // Check if demo PIN is required
      const settingsResponse = await api.get<any>('/app-settings');
      if (settingsResponse?.success && settingsResponse?.settings?.demoPinCode) {
        // Demo PIN is configured, show the PIN screen
        setStep('demo-pin');
        return;
      }
      // No PIN required, proceed to role selection
      setStep('role');
    } catch (error) {
      console.warn('[Login] Failed to check demo PIN requirement:', error);
      // If we can't check settings, proceed to role selection anyway
      setStep('role');
    }
  };

  const handleDemoPinSuccess = () => {
    setStep('role');
  };

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pinCode.trim()) {
      setError('Please enter the PIN code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post<VerifyPinResponse>('/app-settings/verify-pin', {
        pinCode,
        pinType: 'demo',
      });

      if ((response as VerifyPinResponse).success) {
        // Store that the user has verified the demo PIN in this session
        sessionStorage.setItem('demoPinEntered', 'true');
        setPinCode('');
        handleDemoPinSuccess();
      } else {
        setError((response as VerifyPinResponse).message || 'Invalid PIN code');
      }
    } catch (err: any) {
      console.error('[Login] Error verifying PIN:', err);
      // Check if no PIN is configured
      if (err.message?.includes('No demo PIN configured')) {
        // No PIN configured, allow access anyway
        sessionStorage.setItem('demoPinEntered', 'true');
        setPinCode('');
        handleDemoPinSuccess();
        return;
      }
      setError('Failed to verify PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    if (!selectedRole) return;

    setLoading(true);
    setError('');

    try {
      await login(selectedRole);

      // After login, check if user is locked by reading from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.isLocked) {
          console.log('[Login] User is locked after demo login, redirecting to locked page');
          navigate('/locked');
          return;
        }
      }

      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login failed:', err);

      // Check if error is due to account being locked
      if (err.message === 'Account locked') {
        console.log('[Login] User account is locked during demo login');
        navigate('/locked');
        return;
      }

      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const redirectUrl = `${window.location.origin}/login`;
      console.log('[Login] Google OAuth redirect URL:', redirectUrl);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        console.error('[Login] Google OAuth error:', error);
        setError('Failed to start Google sign in');
        setLoading(false);
      }
      // If successful, Supabase will redirect to Google
    } catch (err) {
      console.error('[Login] Google login error:', err);
      setError('Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const redirectUrl = `${window.location.origin}/login`;
      console.log('[Login] Apple OAuth redirect URL:', redirectUrl);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        console.error('[Login] Apple OAuth error:', error);
        setError('Failed to start Apple sign in');
        setLoading(false);
      }
      // If successful, Supabase will redirect to Apple
    } catch (err) {
      console.error('[Login] Apple login error:', err);
      setError('Failed to sign in with Apple');
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-full p-4 shadow-lg">
              <Droplet className="h-12 w-12 text-cyan-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AquaFlow</h1>
          <p className="text-gray-600">Water Management Platform</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 'method' ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Welcome Back</h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {/* Google Sign In */}
                <button
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors font-medium"
                >
                  Sign in with Google
                </button>

                {/* Apple Sign In */}
                <button
                  onClick={handleAppleLogin}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-900 rounded-lg text-white bg-gray-900 hover:bg-gray-800 transition-colors font-medium"
                >
                  Sign in with Apple
                </button>

                {/* Divider - Only show in Vibecode preview */}
                {isVibecodePreview && (
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or</span>
                    </div>
                  </div>
                )}

                {/* Demo Mode - Only in Vibecode preview */}
                {isVibecodePreview && (
                  <button
                    onClick={handleDemoModeClick}
                    className="w-full flex items-center justify-center px-4 py-3 border border-cyan-600 rounded-lg text-cyan-700 bg-cyan-50 hover:bg-cyan-100 transition-colors font-medium"
                  >
                    Demo Mode
                  </button>
                )}
              </div>

              {isVibecodePreview && (
                <p className="mt-6 text-center text-sm text-gray-500">
                  Demo mode uses sample data for testing
                </p>
              )}
            </>
          ) : step === 'demo-pin' ? (
            <>
              {/* Header with Icon */}
              <div className="flex justify-center mb-6">
                <div className="p-3 bg-cyan-100 rounded-full">
                  <Lock size={36} className="text-cyan-600" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
                Demo Mode Access
              </h2>

              <p className="text-center text-gray-600 mb-8">
                Enter the PIN code to access demo mode.
              </p>

              {/* Form */}
              <form onSubmit={handleVerifyPin} className="space-y-6">
                <div>
                  <label htmlFor="demo-pin" className="block text-sm font-medium text-gray-700 mb-2">
                    PIN Code
                  </label>
                  <input
                    id="demo-pin"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={pinCode}
                    onChange={(e) => {
                      setPinCode(e.target.value);
                      setError('');
                    }}
                    disabled={loading}
                    placeholder="Enter PIN"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-cyan-500 text-center text-2xl font-bold tracking-widest disabled:bg-gray-100"
                    autoFocus
                  />
                  {error && (
                    <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify PIN'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep('method');
                    setPinCode('');
                    setError('');
                  }}
                  disabled={loading}
                  className="w-full py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Back
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Select Your Role</h2>
              <p className="text-gray-600 text-center mb-6">Choose a role to explore the demo</p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2 mb-6">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRole(role.id)}
                    className={`w-full text-left px-4 py-3 border rounded-lg transition-colors ${
                      selectedRole === role.id
                        ? 'border-cyan-600 bg-cyan-50 ring-2 ring-cyan-600'
                        : 'border-gray-300 hover:border-cyan-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{role.name}</div>
                    <div className="text-sm text-gray-500">{role.description}</div>
                  </button>
                ))}
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleDemoLogin}
                  disabled={!selectedRole || loading}
                  className="w-full flex items-center justify-center px-4 py-3 rounded-lg text-white bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>

                <button
                  onClick={() => {
                    setStep('method');
                    setSelectedRole('');
                    setError('');
                  }}
                  className="w-full px-4 py-3 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors font-medium"
                >
                  Back
                </button>
              </div>

              <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 text-center">
                  Demo mode uses sample data. Your changes won&apos;t affect real users.
                </p>
              </div>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          WEWEB LLC.
        </p>
      </div>
    </div>
  );
}
